/**
 * CheckpointsTable — the checkpoints this model was trained through.
 *
 * Rows come from `GET /api/models/{id}/checkpoints`: every distinct
 * `model_slug` a pipeline scored on the same task set the served model was
 * scored on. There is no Step column because eval runs carry no step
 * number — a rank invented from row order would read as one.
 */

import type { CheckpointRow, CheckpointsResponse } from '@/api/generated/model'
import { formatScore } from '@/components/evals/format'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

const STATE_VARIANT: Record<
  CheckpointRow['state'],
  'success' | 'outline' | 'secondary'
> = {
  serving: 'success',
  base: 'outline',
  archived: 'secondary',
}

interface CheckpointsTableProps {
  checkpoints: CheckpointsResponse
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

export function CheckpointsTable({
  checkpoints,
}: CheckpointsTableProps): React.JSX.Element {
  if (checkpoints.checkpoints.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No checkpoints scored yet. Each eval run naming a checkpoint adds a
        row here.
      </p>
    )
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Checkpoint</TableHead>
          <TableHead>Score</TableHead>
          <TableHead className="hidden md:table-cell">Scored on</TableHead>
          <TableHead>Scored</TableHead>
          <TableHead>State</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {checkpoints.checkpoints.map((checkpoint) => (
          <TableRow key={checkpoint.model_slug}>
            <TableCell className="font-mono text-xs">
              {checkpoint.model_slug}
            </TableCell>
            <TableCell className="font-mono text-xs tabular-nums">
              {formatScore(checkpoint.score, checkpoints.score_format)}
            </TableCell>
            <TableCell className="hidden text-sm text-muted-foreground md:table-cell">
              {checkpoints.group_display_name} · {checkpoint.task_count} tasks
            </TableCell>
            <TableCell className="text-sm">
              {formatDate(checkpoint.scored_at)}
            </TableCell>
            <TableCell>
              <Badge variant={STATE_VARIANT[checkpoint.state]}>
                {checkpoint.state}
              </Badge>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
